/**
 * The **Arena Master gump** (PRD #334, ADR-0245): a draggable in-world window the
 * player opens by **double-clicking the Arena Master NPC**, with three panes -
 *
 *  1. **Leaderboard** - the complete paged Arena ranking, reusing the existing
 *     `?board=arena` leaderboard read (Slice A, ADR-0172).
 *  2. **Join Arena** - a persistent button that opens a dedicated mode gump.
 *     Choosing a mode there sends the existing `StartArenaRun` command.
 *  3. **Shop tab** - a static, inert placeholder ("wares coming soon"); a follow-up
 *     wires real purchasable wares (a purchase command + currency + item defs).
 *
 * It is a **client-only** affordance: it reads the same public read-only
 * `GET /leaderboard?board=arena` HTTP read the leaderboard window reads, and sends
 * only the existing `StartArenaRun` command (through an injected `onJoinArena`
 * callback `main.ts` guards on `playerId`). No server, protocol, or persistence
 * change - `StartArenaRun` and the arena board already exist end-to-end.
 *
 * Structurally it is the **`src/leaderboardWindow.ts` twin**: a fixed-position DOM
 * overlay (a sibling of the `#app` canvas, not a Pixi in-world gump), plain elements
 * with `textContent` (never `innerHTML`, so a reserved name can't inject markup),
 * first-open centring, pointer-drag by the title bar, and a monotonic fetch stamp so
 * a stale in-flight page can't repaint after a close/reopen. It is **not** part of
 * the toolbar one-panel-at-a-time group (`src/toolbar.ts`); it is an independent
 * window that may coexist with the leaderboard window, opened only from the NPC.
 */
import {
  formatArenaScore,
  LEADERBOARD_PAGE_SIZE,
  type ArenaLeaderboardEntry,
  type CardDuelLeaderboardEntry,
  type LegacyCardDuelLeaderboardEntry,
  type DuelLeaderboardEntry,
  type GoldLeaderboardEntry,
  type LeaderboardBoard,
  type LeaderboardEntry,
  type LeaderboardPage,
  type StormMarksLeaderboardEntry,
  type SphereLeaderboardEntry,
} from './leaderboard';
import { createTabs } from './ui/tabs';
import { createUiWindow } from './ui/window';
import type { ArenaHistoryPage, ArenaRunHistoryEntry } from './arenaHistory';
import {
  ARENA_CHALLENGES,
  DEFAULT_ARENA_CHALLENGE_ID,
  type ArenaChallengeId,
} from './sim/arenaChallenges';

/**
 * The Arena board's empty note - the run-the-arena nudge, matching the Arena tab's
 * wording in `leaderboardWindow.ts` (NO_ARENA_TEXT) so the two surfaces read the same.
 */
const NO_ARENA_TEXT = 'No Arena runs yet — reserve a name and step through the Arena Gate.';
/** Shown when the standings fetch fails (server down / network error) - never a blank panel. */
const UNAVAILABLE_TEXT = 'Standings are unavailable right now.';

/**
 * How often the open gump re-polls the `?board=arena` page. Matches the leaderboard
 * window's cadence (a few seconds keeps the standings visibly live without hammering
 * the single-VM server - each poll is one fixed-size page). Exported so a test can
 * drive the poll without hard-coding the interval.
 */
export const ARENA_GUMP_REFRESH_MS = 5_000;

/**
 * A row of any board, as the injected fetcher carries them. The gump only ever asks
 * for `?board=arena`, but this broad union (the leaderboard window's `AnyEntry`) keeps
 * the `fetchPage` dep structurally compatible with `main.ts`'s shared
 * `fetchLeaderboardPage`, exactly as `initLeaderboardWindow` receives it.
 */
type AnyEntry =
  | LeaderboardEntry
  | GoldLeaderboardEntry
  | DuelLeaderboardEntry
  | StormMarksLeaderboardEntry
  | ArenaLeaderboardEntry
  | CardDuelLeaderboardEntry
  | LegacyCardDuelLeaderboardEntry
  | SphereLeaderboardEntry;

/** Collaborators injected by `main.ts` (kept thin so the gump is unit-testable). */
export interface ArenaGumpDeps {
  /**
   * Fetch one page of a ranked board (the gump always asks `board: 'arena'`), or
   * `null` on any failure - `main.ts` passes its `fetchLeaderboardPage`, the same
   * fetcher the leaderboard window receives. Called on open and each poll tick.
   */
  fetchPage: (
    offset: number,
    me?: string,
    board?: LeaderboardBoard,
    season?: 'last' | number,
    challenge?: ArenaChallengeId,
  ) => Promise<LeaderboardPage<AnyEntry> | null>;
  /**
   * The viewer's own display name as typed on the splash (empty when not yet
   * entered). Optional; when present it rides each fetch as `?me=` (harmless - the
   * gump renders no self-highlight), read per fetch so a retry with an edited name
   * is picked up automatically.
   */
  viewerName?: () => string;
  /**
   * Start an arena run after the dedicated Join Arena gump chooses a mode.
   * `main.ts` wires it to `activeClient.sendCommand({ kind: 'StartArenaRun',
   * entity: playerId, challengeId })` (guarded on `playerId`).
   * The server rules authoritatively on entry, so no client-side rejection handling is needed.
   */
  onJoinArena: (challengeId: ArenaChallengeId) => void;
  requestHistory?: (before?: number) => void;
  /** The document to wire against; defaults to the global `document` (jsdom-testable). */
  doc?: Document;
}

/** The handle `main.ts` keeps to open the gump from the NPC double-click. */
export interface ArenaGumpHandle {
  /** Show the gump (centring it on first open) and re-fetch the arena standings. */
  open(): void;
  /** Hide the gump. A right-click on the gump, the ✕, and Escape do this too. */
  close(): void;
  /** Whether the gump is currently shown. */
  isOpen(): boolean;
  historyPage(page: ArenaHistoryPage): void;
}

/** Build the gump's content (same ids the CSS binds to) when index.html no longer carries it. */
function ensureContent(doc: Document, root: HTMLElement): void {
  if (doc.getElementById('arena-gump-board-list')) {
    ensureLeaderboardControls(doc);
    const joinButton = doc.getElementById('arena-gump-join-button');
    if (joinButton) joinButton.textContent = 'Join Arena';
    const tabBar = doc.getElementById('arena-gump-tabs');
    const shopTab = doc.getElementById('arena-gump-tab-shop');
    // The Arena Master is strictly arena content: adopted markup that still
    // carries the retired Sphere Duels tab loses it (that board lives in the
    // leaderboard window).
    doc.getElementById('arena-gump-tab-sphere')?.remove();
    if (tabBar && !doc.getElementById('arena-gump-tab-history')) {
      const tab = doc.createElement('button'); tab.id = 'arena-gump-tab-history';
      tab.type = 'button'; tab.textContent = 'My History'; tabBar.insertBefore(tab, shopTab);
    }
    const body = doc.getElementById('arena-gump-body');
    if (body && !doc.getElementById('arena-gump-history')) {
      const history = doc.createElement('section'); history.id = 'arena-gump-history'; history.hidden = true;
      const state = doc.createElement('p'); state.id = 'arena-gump-history-state'; state.textContent = 'Loading history…';
      const list = doc.createElement('div'); list.id = 'arena-gump-history-list';
      const more = doc.createElement('button'); more.id = 'arena-gump-history-more'; more.type = 'button';
      more.textContent = 'Older runs'; more.hidden = true; history.append(state, list, more); body.appendChild(history);
    }
    return;
  }
  const body = doc.createElement('div');
  body.id = 'arena-gump-body';
  const tabBar = doc.createElement('div');
  tabBar.id = 'arena-gump-tabs';
  const leaderboardTab = doc.createElement('button');
  leaderboardTab.id = 'arena-gump-tab-leaderboard';
  leaderboardTab.type = 'button';
  leaderboardTab.textContent = 'Leaderboard';
  const shopTab = doc.createElement('button');
  shopTab.id = 'arena-gump-tab-shop';
  shopTab.type = 'button';
  shopTab.textContent = 'Shop';
  const historyTab = doc.createElement('button');
  historyTab.id = 'arena-gump-tab-history'; historyTab.type = 'button'; historyTab.textContent = 'My History';
  tabBar.append(leaderboardTab, historyTab, shopTab);
  // Pane 1: the full paged ?board=arena ranking.
  const board = doc.createElement('section');
  board.id = 'arena-gump-board';
  const boardTitle = doc.createElement('h3');
  boardTitle.textContent = 'Arena Leaderboard';
  const leaderboardControls = createLeaderboardControls(doc, boardTitle);
  const list = doc.createElement('ol');
  list.id = 'arena-gump-board-list';
  const empty = doc.createElement('p');
  empty.id = 'arena-gump-board-empty';
  empty.hidden = true;
  const pager = doc.createElement('div');
  pager.id = 'arena-gump-pager';
  const prev = doc.createElement('button');
  prev.id = 'arena-gump-prev';
  prev.type = 'button';
  prev.textContent = '‹';
  prev.setAttribute('aria-label', 'Previous page');
  const page = doc.createElement('span');
  page.id = 'arena-gump-page';
  const next = doc.createElement('button');
  next.id = 'arena-gump-next';
  next.type = 'button';
  next.textContent = '›';
  next.setAttribute('aria-label', 'Next page');
  pager.append(prev, page, next);
  board.append(leaderboardControls, list, empty, pager);
  // Pane 2: the Join button.
  const join = doc.createElement('section');
  join.id = 'arena-gump-join';
  const joinBtn = doc.createElement('button');
  joinBtn.id = 'arena-gump-join-button';
  joinBtn.type = 'button';
  joinBtn.textContent = 'Join Arena';
  join.appendChild(joinBtn);
  // Pane 3: the Shop - an INERT placeholder (PRD #334): no purchase mechanic,
  // currency, or item definitions yet. TODO(arena-shop): drop real purchasable
  // wares into #arena-gump-shop-items behind a purchase command + currency (a
  // follow-up slice); the slots below are non-interactive.
  const shop = doc.createElement('section');
  shop.id = 'arena-gump-shop';
  shop.hidden = true;
  const shopTitle = doc.createElement('h3');
  shopTitle.textContent = 'Shop';
  const shopNote = doc.createElement('p');
  shopNote.id = 'arena-gump-shop-note';
  shopNote.textContent = 'Wares coming soon…';
  const slots = doc.createElement('div');
  slots.id = 'arena-gump-shop-items';
  for (let i = 0; i < 3; i++) {
    const slot = doc.createElement('div');
    slot.className = 'arena-shop-slot';
    slot.setAttribute('aria-disabled', 'true');
    slots.appendChild(slot);
  }
  shop.append(shopTitle, shopNote, slots);
  const history = doc.createElement('section'); history.id = 'arena-gump-history'; history.hidden = true;
  const historyState = doc.createElement('p'); historyState.id = 'arena-gump-history-state';
  historyState.textContent = 'Loading history…';
  const historyList = doc.createElement('div'); historyList.id = 'arena-gump-history-list';
  const historyMore = doc.createElement('button'); historyMore.id = 'arena-gump-history-more';
  historyMore.type = 'button'; historyMore.textContent = 'Older runs'; historyMore.hidden = true;
  history.append(historyState, historyList, historyMore);
  body.append(board, history, shop);
  root.append(tabBar, body, join);
}

function createLeaderboardControls(doc: Document, title: HTMLElement): HTMLElement {
  const controls = doc.createElement('div');
  controls.id = 'arena-gump-leaderboard-controls';
  const heading = doc.createElement('div');
  heading.id = 'arena-gump-board-heading';
  const seasonLabel = doc.createElement('label');
  seasonLabel.id = 'arena-gump-season-label';
  seasonLabel.append('Season ');
  const season = doc.createElement('select');
  season.id = 'arena-gump-season';
  const current = doc.createElement('option');
  current.value = 'current';
  current.textContent = 'Current season';
  season.appendChild(current);
  seasonLabel.appendChild(season);
  heading.append(title, seasonLabel);

  const modes = doc.createElement('div');
  modes.id = 'arena-gump-modes';
  for (const challenge of ARENA_CHALLENGES.filter((entry) => entry.available)) {
    const button = doc.createElement('button');
    button.id = `arena-gump-mode-${challenge.id}`;
    button.type = 'button';
    button.textContent = challenge.id === DEFAULT_ARENA_CHALLENGE_ID
      ? 'Classic'
      : challenge.name;
    modes.appendChild(button);
  }
  const description = doc.createElement('p');
  description.id = 'arena-gump-challenge-description';
  controls.append(heading, modes, description);
  return controls;
}

function ensureLeaderboardControls(doc: Document): void {
  if (doc.getElementById('arena-gump-leaderboard-controls')) return;
  const board = doc.getElementById('arena-gump-board');
  const list = doc.getElementById('arena-gump-board-list');
  const title = board?.querySelector('h3');
  if (!board || !list || !title) return;
  doc.getElementById('arena-gump-challenge-picker')?.remove();
  doc.getElementById('arena-gump-season-toggle')?.remove();
  board.insertBefore(createLeaderboardControls(doc, title), list);
}

interface ArenaJoinGumpHandle {
  open(): void;
  close(): void;
}

function initArenaJoinGump(
  doc: Document,
  onJoinArena: (challengeId: ArenaChallengeId) => void,
): ArenaJoinGumpHandle {
  const win = createUiWindow({
    doc,
    id: 'arena-join-gump',
    title: 'Join Arena',
    ariaLabel: 'Choose Arena mode',
    fallbackWidth: 360,
    fallbackHeight: 260,
    escapeCloses: true,
  });
  if (!win) return { open: () => {}, close: () => {} };

  if (!doc.getElementById('arena-join-modes')) {
    const body = doc.createElement('div');
    body.id = 'arena-join-body';
    const prompt = doc.createElement('p');
    prompt.id = 'arena-join-prompt';
    prompt.textContent = 'Choose your Arena mode.';
    const modes = doc.createElement('div');
    modes.id = 'arena-join-modes';
    for (const challenge of ARENA_CHALLENGES.filter((entry) => entry.available)) {
      const button = doc.createElement('button');
      button.id = `arena-join-mode-${challenge.id}`;
      button.type = 'button';
      const name = doc.createElement('strong');
      name.textContent = challenge.id === DEFAULT_ARENA_CHALLENGE_ID
        ? 'Classic'
        : challenge.name;
      const description = doc.createElement('span');
      description.textContent = challenge.description;
      button.append(name, description);
      button.addEventListener('click', () => {
        onJoinArena(challenge.id);
        win.close();
      });
      modes.appendChild(button);
    }
    body.append(prompt, modes);
    win.root.appendChild(body);
  }

  return { open: win.open, close: win.close };
}

/**
 * Build (or adopt) the `#arena-gump` overlay as a draggable,
 * right-click/Escape/✕-to-close Arena Master gump on the shared
 * {@link createUiWindow} chrome (Chassis Slice H, PRD #534 D7): the markup (same
 * element ids the CSS in `index.html` binds to) is built here at init unless a
 * pre-existing `#arena-gump` is adopted. The **shop pane is inert placeholder
 * content** the gump never touches, so it needs no wiring here. A body-less
 * document degrades to a harmless no-op handle so a bare client never throws.
 */
export function initArenaGump(deps: ArenaGumpDeps): ArenaGumpHandle {
  const doc = deps.doc ?? document;

  let opened = false;
  /**
   * Monotonic fetch stamp: each refresh takes the next id and only the newest is
   * allowed to render, so an older in-flight page can't repaint after the gump is
   * closed and reopened (the leaderboard window's staleness guard).
   */
  let fetchSeq = 0;
  let offset = 0;
  let pollTimer: ReturnType<typeof setInterval> | undefined;

  const win = createUiWindow({
    doc,
    id: 'arena-gump',
    title: 'Arena Master',
    ariaLabel: 'Arena Master',
    fallbackWidth: 300,
    fallbackHeight: 320,
    // Escape closes the gump while open (only then, so it never swallows a stray one).
    escapeCloses: true,
    onOpen() {
      opened = true;
      offset = 0;
      // Fetch immediately, then poll on a steady cadence so the board stays live while
      // the gump is up. Guard the timer so re-opening an already-open gump doesn't
      // stack a second interval.
      refresh();
      pollTimer ??= setInterval(refresh, ARENA_GUMP_REFRESH_MS);
    },
    onClose() {
      opened = false;
      // Stop polling the moment the gump shuts: no leaked timer, no fetching while closed.
      if (pollTimer !== undefined) {
        clearInterval(pollTimer);
        pollTimer = undefined;
      }
    },
  });
  if (!win) return { open: () => {}, close: () => {}, isOpen: () => false, historyPage: () => {} };
  ensureContent(doc, win.root);
  const list = doc.getElementById('arena-gump-board-list');
  const empty = doc.getElementById('arena-gump-board-empty');
  const joinBtn = doc.getElementById('arena-gump-join-button') as HTMLButtonElement | null;
  const prevBtn = doc.getElementById('arena-gump-prev') as HTMLButtonElement | null;
  const nextBtn = doc.getElementById('arena-gump-next') as HTMLButtonElement | null;
  const pageLabel = doc.getElementById('arena-gump-page');
  const boardPane = doc.getElementById('arena-gump-board');
  const challengeDescription = doc.getElementById('arena-gump-challenge-description');
  const seasonSelect = doc.getElementById('arena-gump-season') as HTMLSelectElement | null;
  const shopPane = doc.getElementById('arena-gump-shop');
  const historyPane = doc.getElementById('arena-gump-history');
  const historyState = doc.getElementById('arena-gump-history-state');
  const historyList = doc.getElementById('arena-gump-history-list');
  const historyMore = doc.getElementById('arena-gump-history-more') as HTMLButtonElement | null;
  if (!list || !empty || !joinBtn || !prevBtn || !nextBtn || !pageLabel || !boardPane || !shopPane) {
    return { open: () => {}, close: () => {}, isOpen: () => false, historyPage: () => {} };
  }
  if (!historyPane || !historyState || !historyList || !historyMore) {
    return { open: () => {}, close: () => {}, isOpen: () => false, historyPage: () => {} };
  }
  const joinGump = initArenaJoinGump(doc, deps.onJoinArena);
  let historyCursor: number | null = null;
  let challengeId: ArenaChallengeId = DEFAULT_ARENA_CHALLENGE_ID;
  let selectedSeason: number | undefined;
  let currentSeason: number | undefined;
  const renderChallenge = (): void => {
    const def = ARENA_CHALLENGES.find((entry) => entry.id === challengeId)!;
    if (challengeDescription) challengeDescription.textContent = def.description;
  };
  const modeTabs = createTabs(
    ARENA_CHALLENGES.filter((entry) => entry.available).map((challenge) => ({
      button: doc.getElementById(`arena-gump-mode-${challenge.id}`),
      value: challenge.id,
    })),
    challengeId,
    (nextChallenge) => {
      challengeId = nextChallenge;
      offset = 0;
      list.replaceChildren();
      renderChallenge();
      refresh();
    },
  );
  void modeTabs;
  renderChallenge();
  seasonSelect?.addEventListener('change', () => {
    selectedSeason =
      seasonSelect.value === 'current' ? undefined : Number(seasonSelect.value);
    offset = 0;
    list.replaceChildren();
    refresh();
  });
  const renderHistory = (page: ArenaHistoryPage): void => {
    historyList.replaceChildren();
    historyState.textContent = page.requiresReservedName
      ? 'Reserve a name to retain and revisit your Arena runs.'
      : page.entries.length === 0 ? 'No Arena runs yet.' : '';
    historyState.hidden = page.entries.length > 0;
    for (const run of page.entries) historyList.appendChild(historyRow(doc, run));
    historyCursor = page.nextCursor;
    historyMore.hidden = historyCursor === null;
  };

  /**
   * Reconcile the top-{@link TOP_RUNS} run rows in place against the latest page,
   * reusing the splash/leaderboard-window `.lb-name`/`.lb-time` row structure so the
   * shared CSS (and the CSS-generated rank number) style it identically. `null` (a
   * failed fetch) on the *first* render shows an unavailable note; a transient poll
   * blip once rows are already on screen leaves them intact rather than blanking the
   * panel out from under the reader.
   */
  const render = (page: LeaderboardPage<AnyEntry> | null): void => {
    if (page === null) {
      if (list.childElementCount === 0) {
        empty.textContent = UNAVAILABLE_TEXT;
        empty.hidden = false;
      }
      return;
    }
    if (
      selectedSeason === undefined
      && typeof page.season === 'number'
      && page.season !== currentSeason
    ) {
      currentSeason = page.season;
      if (seasonSelect) {
        seasonSelect.replaceChildren();
        for (let season = currentSeason; season >= 1; season--) {
          const option = doc.createElement('option');
          option.value = season === currentSeason ? 'current' : String(season);
          option.textContent = season === currentSeason
            ? `Season ${season} (Current)`
            : `Season ${season}`;
          seasonSelect.appendChild(option);
        }
        seasonSelect.value = 'current';
      }
    }
    const entries = page.entries as ArenaLeaderboardEntry[];
    list.style.counterReset = `rank ${page.offset}`;
    for (const [i, entry] of entries.entries()) {
      let li = list.children[i] as HTMLLIElement | undefined;
      if (!li) {
        li = doc.createElement('li');
        const name = doc.createElement('span');
        name.className = 'lb-name';
        const score = doc.createElement('span');
        score.className = 'lb-time';
        li.append(name, score);
        list.appendChild(li);
      }
      const name = li.querySelector('.lb-name')!;
      const score = li.querySelector('.lb-time')!;
      const nameText = entry.name;
      const scoreText = formatArenaScore(entry.bestScore);
      // textContent (never innerHTML) so a reserved name can't inject markup, and
      // guarded so an unchanged cell isn't needlessly rewritten on the poll.
      if (name.textContent !== nameText) name.textContent = nameText;
      if (score.textContent !== scoreText) score.textContent = scoreText;
    }
    // Drop any surplus rows left from a now-shorter board (e.g. runs fell off the top).
    while (list.childElementCount > entries.length) {
      list.removeChild(list.lastElementChild!);
    }
    empty.textContent =
      selectedSeason !== undefined && page.season === null
        ? 'No standings were archived for this season.'
        : NO_ARENA_TEXT;
    empty.hidden = page.total > 0;
    const pageCount = Math.max(1, Math.ceil(page.total / LEADERBOARD_PAGE_SIZE));
    pageLabel.textContent = `Page ${Math.floor(page.offset / LEADERBOARD_PAGE_SIZE) + 1} of ${pageCount}`;
    prevBtn.disabled = page.offset === 0;
    nextBtn.disabled = page.offset + LEADERBOARD_PAGE_SIZE >= page.total;
  };

  /**
   * Pull the current arena page once and render it. A resolve is ignored when the
   * gump has closed meanwhile *or* a newer fetch has been issued (a close/reopen), so
   * a stale page never renders.
   */
  const refresh = (): void => {
    const seq = ++fetchSeq;
    void deps
      .fetchPage(
        offset,
        deps.viewerName?.() || undefined,
        'arena',
        selectedSeason,
        challengeId,
      )
      .then((page) => {
        if (!opened || seq !== fetchSeq) return;
        render(page);
      });
  };

  const tabs = createTabs(
    [
      { button: doc.getElementById('arena-gump-tab-leaderboard'), value: 'leaderboard' },
      { button: doc.getElementById('arena-gump-tab-history'), value: 'history' },
      { button: doc.getElementById('arena-gump-tab-shop'), value: 'shop' },
    ] as const,
    'leaderboard',
    (tab) => {
      boardPane.hidden = tab !== 'leaderboard';
      historyPane.hidden = tab !== 'history';
      shopPane.hidden = tab !== 'shop';
      if (tab === 'leaderboard') {
        offset = 0;
        list.replaceChildren();
        refresh();
      }
      if (tab === 'history') { historyState.hidden = false; historyState.textContent = 'Loading history…'; deps.requestHistory?.(); }
    },
  );
  void tabs;

  prevBtn.addEventListener('click', () => {
    offset = Math.max(0, offset - LEADERBOARD_PAGE_SIZE);
    refresh();
  });
  nextBtn.addEventListener('click', () => {
    offset += LEADERBOARD_PAGE_SIZE;
    refresh();
  });
  historyMore.addEventListener('click', () => { if (historyCursor !== null) deps.requestHistory?.(historyCursor); });

  // Joining is a two-step choice. The leaderboard mode is only a board filter,
  // while this dedicated gump owns the mode sent with StartArenaRun.
  joinBtn.addEventListener('click', () => {
    win.close();
    joinGump.open();
  });

  return {
    open: () => {
      joinGump.close();
      win.open();
    },
    close: () => {
      win.close();
      joinGump.close();
    },
    isOpen: () => opened,
    historyPage: renderHistory,
  };
}

function historyRow(doc: Document, run: ArenaRunHistoryEntry): HTMLElement {
  const details = doc.createElement('details'); details.className = 'arena-history-row';
  const summary = doc.createElement('summary');
  const seconds = Math.floor((run.elapsedTicks ?? 0) / 20);
  const challenge = ARENA_CHALLENGES.find((entry) => entry.id === run.challengeId);
  summary.textContent = `${new Date(run.startedAt).toLocaleDateString()} · ${challenge?.name ?? run.challengeId} v${run.challengeVersion} · ${run.classId} · ${seconds}s · ${run.score ?? '—'} pts · ${run.kills ?? '—'} kills · W${run.wave ?? '—'} L${run.level ?? '—'}`;
  const body = doc.createElement('p');
  body.textContent = `${run.endReason ?? 'In progress'} · ${run.payout ?? '—'} gold · Bosses ${run.bossKills ?? '—'} (${Math.round((run.bossProgress ?? 0) * 100)}%) · Season ${run.season} · ${run.rulesetVersion}`;
  const build = doc.createElement('pre'); build.textContent = run.build === null ? 'Build unavailable' : JSON.stringify(run.build, null, 2);
  details.append(summary, body, build); return details;
}
